import { useCallback } from "react";

import { useDispatch, useSelector } from "react-redux";

import { swSettings } from "app/common/services/swSettings";
import type { Size } from "app/custom-hooks/useWIndowSize";
import { useWindowSize } from "app/custom-hooks/useWIndowSize";
import type { OverviewWidgetId } from "app/pages/sales-intelligence/modules/extension-management/types";
import { setHighlightedRowDomains } from "app/pages/sales-intelligence/modules/leads-search";
import type { LeadCellRecord } from "app/pages/sales-intelligence/modules/leads-search/types/rows";
import { useCompanyQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useCompanyQuota";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { toggleFiltersPanelAction } from "app/pages/sales-intelligence/sub-modules/advanced-search/store/action-creators";
import { MediaQueryBreakpoint } from "app/pages/sales-intelligence/types/styles";
import {
    setWidgetIdRightBar,
    toggleRightBar,
} from "app/pages/workspace/sales/sub-modules/common/store/action-creators";
import { selectRightBarIsOpen } from "app/pages/workspace/sales/sub-modules/common/store/selectors";
import { selectWebsiteThunk } from "app/pages/workspace/sales/sub-modules/opportunities-lists/store/effects";
import { selectActiveWebsite } from "app/pages/workspace/sales/sub-modules/opportunities-lists/store/selectors";
import { isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import { selectIsFiltersPanelExpanded } from "../../../leads-search-filters";
import { getLeadDomainFromRow } from "../../utils/rows";

export type HookParams = {
    record: LeadCellRecord;
    widgetId?: OverviewWidgetId;
    onToggleRightSidebar?: (isOpen: boolean, domain: string) => void;
};

type HookValue = {
    onToggleSidebar: () => void;
    isSidebarDisabled: boolean;
};

export const useLeadCellActions = ({ record, widgetId, onToggleRightSidebar }: HookParams): HookValue => {
    const dispatch = useDispatch();
    const { isQuotaReached } = useCompanyQuota();
    const isDataCreditEnabled = salesSettingsHelper.isDataCreditsEnabled;
    const isNTUser = isNoTouch(swSettings);

    const selectedWebsite = useSelector(selectActiveWebsite);
    const isRightSidebarOpen = useSelector(selectRightBarIsOpen);
    const isFiltersPanelExpanded = useSelector(selectIsFiltersPanelExpanded);
    const isDisabledRightSidebar = isDataCreditEnabled && isNTUser ? false : isQuotaReached;

    const { width: windowWidth = 0 }: Size = useWindowSize();

    const handleToggleRightSidebar = useCallback((): void => {
        if (isDisabledRightSidebar) {
            return;
        }
        const domain = getLeadDomainFromRow(record);

        const isSidebarOpen = selectedWebsite?.domain !== domain || !isRightSidebarOpen;

        if (widgetId) {
            dispatch(setWidgetIdRightBar(widgetId));
        }

        dispatch(selectWebsiteThunk({ domain: domain, favicon: record.favicon ?? "" }));

        dispatch(toggleRightBar(isSidebarOpen));
        onToggleRightSidebar?.(isSidebarOpen, domain);

        dispatch(setHighlightedRowDomains(isSidebarOpen ? [domain] : []));

        if (isFiltersPanelExpanded && windowWidth <= MediaQueryBreakpoint.LG) {
            dispatch(toggleFiltersPanelAction());
        }
    }, [
        record,
        widgetId,
        selectedWebsite?.domain,
        isRightSidebarOpen,
        onToggleRightSidebar,
        isFiltersPanelExpanded,
        windowWidth,
        isQuotaReached,
    ]);

    return {
        onToggleSidebar: handleToggleRightSidebar,
        isSidebarDisabled: isDisabledRightSidebar,
    };
};
